import { ReactNode, useMemo } from "react";

import { Empty } from "antd";
import uniqBy from "lodash/uniqBy";

import {
    RVFormFloatingLabelWrap,
    HANDLEBAR_TRIGGERS,
    HANDLEBARS_WRAP,
    ConfigurableVariableOption,
    ConfigurableVariablesMenuPickerPlugin,
    JSONMenuTriggerPlugin,
    VariableMenuTriggerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
} from "@regal-voice/ui";
import { useTriggerEventNode } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useTriggerEventNode";
import { useContactAttributesFullListQuery } from "ServerData/EventSchema/Hooks/useContactAttributesQuery/useContactAttributesQuery";
import { useEventsAttributesFullListQuery } from "ServerData/EventSchema/Hooks/useEventsAttributesQuery/useEventsAttributesQuery";
import {
    createOptionsWithPrefixWithOriginalFieldName,
    toContactsFieldLookup,
} from "ServerData/EventSchema/Utils/eventSchemaUtils";
import { testOpenAIPrompt } from "Services/Journeys.service";
import { DecisionAiVariant } from "Types/Journey";
import { OpenAIModels } from "Types/OpenAi";

import sharedStyles from "../../../SharedDemoStyles.module.scss";

import styles from "./DecisionAILexicalInput.module.scss";

const HANDLEBARS_REGEX_LIST = [HANDLEBARS_WRAP];

type InputProps = {
    name: string;
    rules?: Array<any>;
    overriddenAt: number;
    prompt: string;
    model: OpenAIModels;
    variants: Array<DecisionAiVariant>;
    formItemHeaderTooltip?: ReactNode;
    emptyState: ReactNode;
    showEmptyState: boolean;
};

const NO_TRIGGER_EVENT_MESSAGE = (
    <div className={styles.emptyContainer}>
        <Empty
            data-testid="not-connected-trigger-event"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No Trigger Events"
            className={styles.emptyIcon}
        />
        <p className={styles.emptyText}>Set event in trigger node to access event properties.</p>
    </div>
);

export function DecisionAILexicalInput({
    name,
    rules,
    overriddenAt,
    prompt,
    model,
    variants,
    formItemHeaderTooltip,
    emptyState,
    showEmptyState,
}: InputProps) {
    const { triggerNode } = useTriggerEventNode();

    // EVENT
    const { loading: eventLoading, eventAttributes } = useEventsAttributesFullListQuery(triggerNode);
    const eventOptions = useMemo(() => {
        if (eventAttributes.length > 0) {
            return eventAttributes.map((event) => createOptionsWithPrefixWithOriginalFieldName(event, "event"));
        }
        return [];
    }, [eventAttributes]);

    // CONTACT
    const { loading: loadingAttrs, contactAttributes } = useContactAttributesFullListQuery({
        formatToObject: false,
    });
    const contactEventOptions = useMemo(() => toContactsFieldLookup(contactAttributes), [contactAttributes]);

    // OPTIONS
    const loading = loadingAttrs || eventLoading;
    const customOptions = useMemo(() => {
        const items = [...contactEventOptions, ...eventOptions].map(
            (option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)
        );
        return uniqBy(items, "value");
    }, [contactEventOptions, eventOptions]);

    // MENU
    const MENU_ITEMS = useMemo(
        () => [
            {
                key: "contact",
                title: "Contact Attributes",
            },
            {
                key: "event",
                title: "Trigger Event Properties",
                disabled: !triggerNode.eventName,
                popoverContent: !triggerNode.eventName ? NO_TRIGGER_EVENT_MESSAGE : undefined,
            },
        ],
        [triggerNode.eventName]
    );

    return (
        <div className={styles.lexicalContainer}>
            <RVFormFloatingLabelWrap
                className={sharedStyles.doubleItem}
                showFormLabel
                formItemHeaderTooltip={formItemHeaderTooltip}
                formItemHeader={"Prompt"}
                label={showEmptyState ? "" : "Prompt"}
                name={name}
                rules={rules}
                validateTrigger={["onSubmit"]}
            >
                {showEmptyState ? (
                    emptyState
                ) : (
                    <PlainTextEditor
                        data-testid="decision-ai-lexical-editor"
                        className={styles.editorHasContainer}
                        inputClassName={styles.inputHasContainer}
                        formItemName={name}
                        key={overriddenAt}
                    >
                        <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
                        <ConfigurableVariablesMenuPickerPlugin
                            popAbove
                            loading={loading}
                            fixedWidth
                            prioritizedTriggers={HANDLEBAR_TRIGGERS}
                            options={customOptions}
                            menuItems={MENU_ITEMS}
                        />
                        <JSONMenuTriggerPlugin tooltip="Dynamic Variables" />
                        <VariableMenuTriggerPlugin
                            tooltip="Test Prompt"
                            prompt={prompt}
                            model={model}
                            variants={variants}
                            customVariables={customOptions}
                            testPrompt={testOpenAIPrompt}
                        />
                    </PlainTextEditor>
                )}
            </RVFormFloatingLabelWrap>
        </div>
    );
}
